/* eslint-disable react/prop-types */
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MapIcon from "@mui/icons-material/Map";

/**
 * DocumentsHeader Component
 *
 * A compact, enterprise-level header for document management pages.
 * Features gradient background, title with icon, stats, and action buttons.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon to display next to title
 * @param {string} props.title - Main title text
 * @param {string} props.subtitle - Subtitle/description text
 * @param {number} props.shownCount - Number of documents currently shown
 * @param {number} props.totalCount - Total number of documents
 * @param {Function} props.onCalendarClick - Handler for calendar button click
 * @param {Function} props.onMapClick - Handler for map button click
 * @param {Function} props.onSimopsClick - Handler for SIMOPS button click (optional)
 * @param {number} props.simopsCount - Count of SIMOPS permits (optional)
 * @param {Function} props.t - Translation function
 * @param {string} props.translationPrefix - Prefix for translation keys
 */
function DocumentsHeader({
  icon,
  title,
  subtitle,
  shownCount,
  totalCount,
  onCalendarClick,
  onMapClick,
  onSimopsClick,
  simopsCount,
  bannerNote,
  t,
  translationPrefix = "documentsPage",
}) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
        py: { xs: 2, sm: 2.5 },
        px: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "150px",
          height: "150px",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.contrastText,
            0.08
          )} 0%, transparent 70%)`,
          borderRadius: "50%",
          transform: "translate(50%, -50%)",
        }}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={{ xs: 2, sm: 0 }}
        sx={{ position: "relative", zIndex: 1 }}
      >
        {/* Left Section: Title, Subtitle, Stats */}
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}
          >
            {icon && <Box sx={{ fontSize: { xs: 20, sm: 24 } }}>{icon}</Box>}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.01em",
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              }}
            >
              {title}
            </Typography>
            {/* Stats Chip */}
            <Chip
              label={`${shownCount}/${totalCount}`}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.75rem",
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
                color: theme.palette.primary.contrastText,
                backdropFilter: "blur(10px)",
                border: `1px solid ${alpha(
                  theme.palette.primary.contrastText,
                  0.3
                )}`,
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.9,
              fontWeight: 400,
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
            }}
          >
            {subtitle}
          </Typography>
        </Box>

        {/* Right Section: Action Buttons */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-start", sm: "flex-end" },
          }}
        >
          {isXs || isSm ? (
            // Mobile/Tablet: Icon buttons only
            <>
              <IconButton
                aria-label={t(`${translationPrefix}.viewCalendarAria`)}
                onClick={onCalendarClick}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.15),
                  color: theme.palette.primary.contrastText,
                  border: `1px solid ${alpha(
                    theme.palette.primary.contrastText,
                    0.2
                  )}`,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.contrastText, 0.25),
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <CalendarTodayIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label={t(`${translationPrefix}.viewMapAria`)}
                onClick={onMapClick}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.15),
                  color: theme.palette.primary.contrastText,
                  border: `1px solid ${alpha(
                    theme.palette.primary.contrastText,
                    0.2
                  )}`,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.contrastText, 0.25),
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <MapIcon fontSize="small" />
              </IconButton>
              {onSimopsClick && (
                <IconButton
                  aria-label={t(`${translationPrefix}.simopsButton`)}
                  onClick={onSimopsClick}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.2),
                    color: theme.palette.warning.contrastText || "#fff",
                    border: `1px solid ${alpha(
                      theme.palette.warning.main,
                      0.3
                    )}`,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.warning.main, 0.3),
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                    position: "relative",
                  }}
                >
                  <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>
                    ⚠️
                  </Typography>
                  {simopsCount > 0 && (
                    <Chip
                      label={simopsCount}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        height: 18,
                        minWidth: 18,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        bgcolor: theme.palette.error.main,
                        color: theme.palette.error.contrastText,
                        "& .MuiChip-label": {
                          px: 0.5,
                        },
                      }}
                    />
                  )}
                </IconButton>
              )}
            </>
          ) : (
            // Desktop: Full buttons with text
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<CalendarTodayIcon />}
                onClick={onCalendarClick}
                aria-label={t(`${translationPrefix}.viewCalendarAria`)}
                sx={{
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.15),
                  color: theme.palette.primary.contrastText,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(
                    theme.palette.primary.contrastText,
                    0.2
                  )}`,
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.contrastText, 0.25),
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {t(`${translationPrefix}.viewCalendarButton`) || "Calendar"}
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<MapIcon />}
                onClick={onMapClick}
                aria-label={t(`${translationPrefix}.viewMapAria`)}
                sx={{
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.15),
                  color: theme.palette.primary.contrastText,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(
                    theme.palette.primary.contrastText,
                    0.2
                  )}`,
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.contrastText, 0.25),
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {t(`${translationPrefix}.viewMapButton`) || "Map"}
              </Button>

              {onSimopsClick && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={onSimopsClick}
                  aria-label={t(`${translationPrefix}.simopsButton`)}
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.2),
                    color: theme.palette.warning.contrastText || "#fff",
                    backdropFilter: "blur(10px)",
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                    px: 2,
                    py: 0.75,
                    borderRadius: 1.5,
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.warning.main, 0.3),
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                    position: "relative",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography sx={{ fontSize: "1rem" }}>⚠️</Typography>
                    <Typography
                      variant="inherit"
                      sx={{ fontWeight: "inherit" }}
                    >
                      {t(`${translationPrefix}.simopsButton`) || "SIMOPS"}
                    </Typography>
                    {simopsCount > 0 && (
                      <Chip
                        label={simopsCount}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          bgcolor: alpha(
                            theme.palette.warning.contrastText || "#fff",
                            0.3
                          ),
                          color: theme.palette.warning.contrastText || "#fff",
                          border: `1px solid ${alpha(
                            theme.palette.warning.contrastText || "#fff",
                            0.4
                          )}`,
                          "& .MuiChip-label": {
                            px: 0.75,
                          },
                        }}
                      />
                    )}
                  </Stack>
                </Button>
              )}
            </>
          )}
        </Stack>
      </Stack>
      {bannerNote ? (
        <Typography
          variant="caption"
          component="p"
          sx={{
            mt: 1.5,
            opacity: 0.85,
            fontWeight: 500,
            position: "relative",
            zIndex: 1,
            maxWidth: "min(100%, 720px)",
          }}
        >
          {bannerNote}
        </Typography>
      ) : null}
    </Box>
  );
}

export default DocumentsHeader;
